import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '../Common/Button'
import { useToast } from '../../context/useToast'
import type { ToastVariant } from '../../context/toastContextCore'

const variantClass: Record<ToastVariant, string> = {
  info: 'is-info',
  success: 'is-success',
  warning: 'is-warning',
  error: 'is-error',
  ghost: 'is-ghost',
}

const variantIcon: Record<ToastVariant, string> = {
  info: 'i',
  success: '✓',
  warning: '!',
  error: '!',
  ghost: '⇄',
}

/**
 * App-wide toast stack. Mounted once near the root; reads the toast queue from
 * ToastProvider. Pinned bottom-center above all panels (z-[100]).
 */
export const ToastViewport = () => {
  const { toasts, dismissToast, focusModeActive } = useToast()

  return (
    <div className="toastViewport">
      <AnimatePresence initial={false}>
        {toasts.filter((toast) => !(focusModeActive && toast.severity === 'critical')).map((toast) => (
          <motion.div
            key={toast.id}
            role="status"
            aria-live="polite"
            layout
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={() => dismissToast(toast.id)}
            className={`toastViewport__item ${variantClass[toast.variant]}`}
          >
            <span className="toastViewport__icon" aria-hidden="true">{variantIcon[toast.variant]}</span>
            <span className="toastViewport__message">{toast.message}</span>
            {toast.action && (
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation()
                  toast.action?.run()
                  dismissToast(toast.id)
                }}
              >
                {toast.action.label}
              </Button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
